import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SERVICE_OPTIONS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", company: "", email: "", phone: "", service: "", message: "", website: "" };

export const ContactForm = ({ compact = false }) => {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Please tell us a little about your project";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(empty);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please try again or email us directly.");
    }
  };

  const inputCls =
    "w-full bg-white/[0.04] border rounded-xl px-4 py-3 text-white placeholder:text-slate-500 text-sm transition-colors duration-200 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange";

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            data-testid="contact-success"
            className="glass rounded-2xl p-10 text-center flex flex-col items-center gap-4"
          >
            <div className="w-16 h-16 rounded-full bg-brand-orange/15 grid place-items-center">
              <CheckCircle2 className="text-brand-orange" size={34} />
            </div>
            <h3 className="font-head text-2xl font-semibold text-white">Message received</h3>
            <p className="text-slate-400 text-sm max-w-sm">
              Thank you for reaching out to ATS IT Solutions. Our team will get back to you within one business day.
            </p>
            <button
              onClick={() => setStatus("idle")}
              data-testid="contact-send-another"
              className="btn-secondary mt-2"
            >
              Send another message
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="space-y-4"
            noValidate
          >
            {/* honeypot */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
              className="hidden"
              aria-hidden="true"
            />

            <div className={`grid ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-2"} gap-4`}>
              <div>
                <input
                  data-testid="contact-name"
                  placeholder="Full Name *"
                  value={form.name}
                  onChange={set("name")}
                  className={`${inputCls} ${errors.name ? "border-red-400" : "border-white/10"}`}
                />
                {errors.name && <p className="text-red-400 text-xs mt-1.5">{errors.name}</p>}
              </div>
              <input
                data-testid="contact-company"
                placeholder="Company Name"
                value={form.company}
                onChange={set("company")}
                className={`${inputCls} border-white/10`}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  data-testid="contact-email"
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={set("email")}
                  className={`${inputCls} ${errors.email ? "border-red-400" : "border-white/10"}`}
                />
                {errors.email && <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>}
              </div>
              <input
                data-testid="contact-phone"
                placeholder="Phone"
                value={form.phone}
                onChange={set("phone")}
                className={`${inputCls} border-white/10`}
              />
            </div>

            <select
              data-testid="contact-service"
              value={form.service}
              onChange={set("service")}
              className={`${inputCls} border-white/10 ${form.service ? "text-white" : "text-slate-500"}`}
            >
              <option value="">Service Interested In</option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s} className="text-navy-deep">
                  {s}
                </option>
              ))}
            </select>

            <div>
              <textarea
                data-testid="contact-message"
                placeholder="Tell us about your project *"
                rows={compact ? 4 : 5}
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none ${errors.message ? "border-red-400" : "border-white/10"}`}
              />
              {errors.message && <p className="text-red-400 text-xs mt-1.5">{errors.message}</p>}
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="contact-submit"
              className="btn-primary w-full sm:w-auto disabled:opacity-70"
            >
              {status === "loading" ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Message <Send size={16} />
                </>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;
